import {FC, useEffect, useState} from 'react'
import {CrewOvertime} from '../core/_requests'

type Props = {
  visible: boolean
  overtime: CrewOvertime | null
  isLoading: boolean
  reviewContext?: 'master' | 'office'
  onClose: () => void
  onSaveRemarks: (officeRemarks: string) => void
  onApprove: (officeRemarks: string) => void
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const CrewOvertimeReviewModal: FC<Props> = ({
  visible,
  overtime,
  isLoading,
  reviewContext = 'office',
  onClose,
  onSaveRemarks,
  onApprove,
}) => {
  const [officeRemarks, setOfficeRemarks] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (!visible || !overtime) return
    setOfficeRemarks(overtime.officeRemarks || '')
    setError('')
  }, [visible, overtime])

  if (!visible || !overtime) return null

  const isMasterReview = reviewContext === 'master'
  const reviewTitle = isMasterReview ? 'Master Review Crew Overtime' : 'Office Review Crew Overtime'
  const remarksLabel = isMasterReview ? 'Master Remarks' : 'Office Remarks'
  const remarksPlaceholder = isMasterReview ? 'Enter master remarks' : 'Enter office remarks'
  const requiredRemarksMessage = isMasterReview ? 'Master remarks are required' : 'Office remarks are required'

  return (
    <div style={WRAP_STYLE}>
      <div className='bg-white rounded shadow-lg' style={{width: '60vw', maxWidth: 900}} onClick={(e) => e.stopPropagation()}>
        <div className='d-flex justify-content-between align-items-center border-bottom px-5 py-4'>
          <h5 className='m-0'>{reviewTitle}</h5>
          <button className='btn-close' onClick={onClose} disabled={isLoading} />
        </div>

        <div className='p-5'>
          <p className='text-muted mb-4'>
            {overtime.crewName} | {overtime.rankName} | {overtime.overtimeMonth?.slice(0, 7)}
          </p>
          <label className='form-label fw-semibold text-dark'>{remarksLabel} <span className='text-danger'>*</span></label>
          <textarea
            className='form-control text-dark bg-white mb-4'
            rows={4}
            value={officeRemarks}
            onChange={(e) => {
              setOfficeRemarks(e.target.value)
              if (error) setError('')
            }}
            placeholder={remarksPlaceholder}
            disabled={isLoading}
          />
          {error ? <div className='text-danger small mt-2'>{error}</div> : null}
        </div>

        <div className='border-top d-flex justify-content-end gap-2 px-5 py-4'>
          <button type='button' className='btn btn-light' onClick={onClose} disabled={isLoading}>Cancel</button>
          <button
            type='button'
            className='btn btn-light-primary'
            onClick={() => {
              if (!officeRemarks.trim()) {
                setError(requiredRemarksMessage)
                return
              }
              onSaveRemarks(officeRemarks)
            }}
            disabled={isLoading}
          >
            Save Remarks
          </button>
          <button
            type='button'
            className='btn btn-success'
            onClick={() => {
              if (!officeRemarks.trim()) {
                setError(requiredRemarksMessage)
                return
              }
              onApprove(officeRemarks)
            }}
            disabled={isLoading || !officeRemarks.trim()}
          >
            Approve
          </button>
        </div>
      </div>
    </div>
  )
}

export default CrewOvertimeReviewModal
